import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Mail, Workflow, Play, Users, Trash2 } from "lucide-react";
import { addDays, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, StatusBadge } from "@/components/Brand";
import CampaignWizard from "@/components/CampaignWizard";
import { EmailPreview } from "@/components/EmailBlocks";
import { api, errMsg, useData } from "@/lib/api";
import { fmtDateTime } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import WorkspaceFilter from "@/components/WorkspaceFilter";

const startOf = (c) => new Date(c.scheduled_at || c.activated_at || c.created_at);

function CampaignDetail({ camp, onClose }) {
  return (
    <Dialog open={!!camp} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="campaign-detail">
        {camp && (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl">{camp.name}</DialogTitle></DialogHeader>
            <div className="flex gap-2 items-center"><StatusBadge status={camp.status} /><span className="text-sm text-stone-500">{camp.type === "drip" ? "Email Sequence" : "Email"} · {camp.recipients.length} recipients</span></div>
            {camp.from_name && <div className="text-xs text-stone-500">From: {camp.from_name} &lt;{camp.from_email}&gt;</div>}
            {camp.type === "drip" ? (
              <div className="space-y-2">
                {camp.steps.map((s, i) => (
                  <div key={i} className="flex gap-3 rounded-lg border p-3 text-sm">
                    <span className="rounded bg-[#3B185F] px-2 py-0.5 text-xs font-bold text-white h-fit">Day {s.day}</span>
                    <div><div className="font-semibold">Email {i + 1}: {s.subject}</div>
                      <div className="text-xs text-stone-500">{camp.status === "Draft" ? "Not scheduled (draft)" : format(addDays(startOf(camp), s.day), "MMM d, yyyy")}</div></div>
                  </div>
                ))}
              </div>
            ) : (
              <EmailPreview subject={camp.subject} blocks={camp.blocks} body={camp.body} contact={camp.recipients[0]} fromName={camp.from_name || "AVY Team"} fromEmail={camp.from_email || "hello@avy.com"} />
            )}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#3B185F] mb-2">Recipients</div>
              <div className="max-h-40 overflow-y-auto divide-y rounded-lg border text-sm" data-testid="campaign-detail-recipients">
                {camp.recipients.map((r) => <div key={r.id} className="flex justify-between p-2"><span>{r.name}</span><span className="text-xs text-stone-500">{r.email}</span></div>)}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Campaigns() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [contacts] = useData("/contacts", []);
  const [campaigns, reload] = useData("/campaigns", []);
  const [associates] = useData(isAdmin ? "/admin/students" : null, []);
  const [workspace, setWorkspace] = useState("all");
  const [wizard, setWizard] = useState({ open: false, ids: [] });
  const [detail, setDetail] = useState(null);
  const shown = workspace === "all" ? campaigns : campaigns.filter((c) => c.owner_id === workspace);

  useEffect(() => {
    const ids = location.state?.recipientIds;
    if (ids?.length) {
      setWizard({ open: true, ids });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const activate = async (c) => {
    try {
      await api.post(`/campaigns/${c.id}/activate`, {});
      toast.success(c.type === "drip" ? "Sequence activated" : "Campaign sent");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const noReply = async (c) => {
    if (!window.confirm(`Mark "${c.name}" complete with no reply? Any Hot recipients will be moved to Cold and logged to their activity history.`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/no-reply`, {});
      toast.success(data.flipped ? `${data.flipped} lead(s) moved Hot → Cold` : "Sequence marked complete — no Hot leads to update");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Email" subtitle="Create one-off campaigns or automated email sequences for your contacts.">
        <WorkspaceFilter associates={associates} value={workspace} onChange={setWorkspace} />
        <Button onClick={() => setWizard({ open: true, ids: [] })} className="bg-[#3B185F]" data-testid="create-campaign-btn"><Plus className="h-4 w-4 mr-1.5" />Create Campaign</Button>
      </PageHeader>

      {!shown.length && <div className="rounded-2xl border border-dashed p-12 text-center text-stone-500" data-testid="campaigns-empty">No campaigns yet. Select contacts and click Create Campaign.</div>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((c) => (
          <div key={c.id} className="rounded-2xl border bg-white p-5 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lg" data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-start justify-between">
              {c.type === "drip" ? <Workflow className="h-6 w-6 text-[#C59B27]" /> : <Mail className="h-6 w-6 text-[#3B185F]" />}
              <StatusBadge status={c.status} testId={`campaign-status-${c.id}`} />
            </div>
            <div className="mt-4 font-display text-xl font-bold text-[#19082B]">{c.name}</div>
            <div className="text-xs uppercase tracking-wider text-stone-500">{c.type === "drip" ? `Sequence · ${c.steps.length} emails` : "One-off email"}</div>
            <div className="mt-4 space-y-1 text-sm text-stone-600">
              <div className="flex items-center gap-2"><Users className="h-4 w-4" /><span data-testid={`campaign-recipients-${c.id}`}>{c.recipients.length} recipients</span></div>
              <div>{c.type === "drip" ? `${c.steps.length} emails · follow-ups on day ${c.steps.slice(1).map((s) => s.day).join(", ")}` : c.subject}</div>
              <div className="text-xs text-stone-400">
                {c.status === "Scheduled" && `Starts ${fmtDateTime(c.scheduled_at)}`}
                {c.status === "Sent" && `Sent ${fmtDateTime(c.sent_at)}`}
                {c.status === "Active" && `Active since ${fmtDateTime(c.activated_at)}`}
                {c.status === "Draft" && `Created ${fmtDateTime(c.created_at)}`}
              </div>
              {c.owner_name && <div className="text-xs text-stone-400">by {c.owner_name}</div>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDetail(c)} data-testid={`campaign-view-${c.id}`}>View</Button>
              {c.status === "Draft" && (
                <Button size="sm" onClick={() => activate(c)} className="bg-[#D4AF37] text-[#19082B] hover:bg-[#C59B27]" data-testid={`campaign-activate-${c.id}`}>
                  <Play className="h-3.5 w-3.5 mr-1" />{c.type === "drip" ? "Activate Sequence" : "Send Now"}
                </Button>
              )}
              {c.type === "drip" && !c.no_reply_processed && ["Active", "Scheduled", "Sent"].includes(c.status) && (
                <Button size="sm" variant="outline" onClick={() => noReply(c)} className="border-sky-300 text-sky-800 hover:bg-sky-50" data-testid={`campaign-no-reply-${c.id}`}>
                  Mark complete — no reply
                </Button>
              )}
              <Button size="sm" variant="ghost" className="ml-auto text-stone-400 hover:text-red-600" onClick={() => remove(c)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <CampaignWizard open={wizard.open} initialIds={wizard.ids} contacts={contacts} onOpenChange={(v) => setWizard((w) => ({ ...w, open: v }))} onCreated={reload} />
      <CampaignDetail camp={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
